import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useLanguage } from "./LanguageContext";
import { ArrowRight, Home, CornerDownRight } from "lucide-react";
import { SEED_CASES } from "./CaseStudiesData";

interface BreadcrumbsProps {
  currentView: "home" | "about-company" | "leadership" | "blog" | "cases" | "proptech";
  isFloatingHUD?: boolean;
}

export default function Breadcrumbs({ currentView, isFloatingHUD = false }: BreadcrumbsProps) {
  const { language, t, currentConfig } = useLanguage();
  const [blogTitle, setBlogTitle] = useState<string>("");
  const [caseTitle, setCaseTitle] = useState<string>("");
  const [currentHash, setCurrentHash] = useState<string>(window.location.hash);

  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      setCurrentHash(hash);
      
      if (hash.startsWith("#blog-post-")) {
        const slug = hash.replace("#blog-post-", "");
        try {
          const saved = localStorage.getItem("teralumax_blog_posts");
          if (saved) {
            const posts = JSON.parse(saved);
            const matched = posts.find((p: any) => p.slug === slug);
            if (matched) {
              const matchedTitle = matched.title[language] || matched.title["en"] || matched.title;
              setBlogTitle(matchedTitle);
              return;
            }
          }
        } catch (e) {
          console.error("Error reading posts from storage for breadcrumbs:", e);
        }
      } else if (hash.startsWith("#case-study-")) {
        const slug = hash.replace("#case-study-", "");
        try {
          const saved = localStorage.getItem("teralumax_case_studies");
          let matched = null;
          if (saved) {
            const cases = JSON.parse(saved);
            matched = cases.find((c: any) => c.slug === slug);
          }
          if (!matched) {
            matched = SEED_CASES.find((c: any) => c.slug === slug);
          }
          if (matched) {
            const matchedTitle = matched.title[language] || matched.title["en"] || matched.title;
            setCaseTitle(matchedTitle);
            return;
          }
        } catch (e) {
          console.error("Error reading cases from storage for breadcrumbs:", e);
        }
      }
      setBlogTitle("");
      setCaseTitle("");
    };

    handleHash();
    window.addEventListener("hashchange", handleHash);
    window.addEventListener("popstate", handleHash);

    return () => {
      window.removeEventListener("hashchange", handleHash);
      window.removeEventListener("popstate", handleHash);
    };
  }, [language, currentView]);

  const hashWithoutAttributes = currentHash.split("-")[0];
  const isHomeSection = currentView === "home" && ["#products", "#philosophy", "#careers", "#contact"].some(h => hashWithoutAttributes.startsWith(h));

  if (currentView === "home" && !isHomeSection) return null;

  const isRTL = currentConfig?.dir === "rtl";
  const isArticleView = currentView === "blog" && currentHash.startsWith("#blog-post-");
  const isCaseView = currentView === "cases" && currentHash.startsWith("#case-study-");

  // Generate dynamic hierarchical items
  const getBreadcrumbItems = () => {
    const items = [
      {
        label: t("home", "navbar") || "HOME",
        href: "#",
        isLast: currentView === "home" && !isHomeSection,
        isHome: true,
      }
    ];

    if (currentView === "home") {
      const cleanHash = currentHash.split("-")[0];
      if (cleanHash === "#products") {
        items.push({
          label: t("products", "navbar") || "PRODUCTS",
          href: "#products",
          isLast: true,
          isHome: false,
        });
      } else if (cleanHash === "#philosophy") {
        items.push({
          label: t("philosophy", "navbar") || "PHILOSOPHY",
          href: "#philosophy",
          isLast: true,
          isHome: false,
        });
      } else if (cleanHash === "#careers") {
        const careersNames: Record<string, string> = {
          en: "CAREERS", es: "CARRERAS", fr: "CARRIÈRES", de: "KARRIERE",
          hi: "करियर", zh: "职业生涯", ja: "採用情報", ar: "الوظائف"
        };
        items.push({
          label: careersNames[language] || "CAREERS",
          href: "#careers",
          isLast: true,
          isHome: false,
        });
      } else if (cleanHash === "#contact") {
        items.push({
          label: t("contact", "navbar") || "CONTACT",
          href: "#contact",
          isLast: true,
          isHome: false,
        });
      }
    } else if (currentView === "proptech") {
      items.push({
        label: t("proptech", "navbar") || "PROPTECH SAAS",
        href: "#proptech",
        isLast: true,
        isHome: false,
      });
    } else if (currentView === "about-company") {
      items.push({
        label: t("aboutCompany", "navbar") || "ABOUT COMPANY",
        href: "#about-company",
        isLast: true,
        isHome: false,
      });
    } else if (currentView === "leadership") {
      items.push({
        label: t("aboutCompany", "navbar") || "ABOUT COMPANY",
        href: "#about-company",
        isLast: false,
        isHome: false,
      });
      items.push({
        label: t("leadership", "navbar") || "LEADERSHIP",
        href: "#leadership",
        isLast: true,
        isHome: false,
      });
    } else if (currentView === "blog") {
      items.push({
        label: t("blog", "navbar") || "BLOG",
        href: "#blog",
        isLast: !isArticleView,
        isHome: false,
      });
    } else if (currentView === "cases") {
      const isCaseView = currentHash.startsWith("#case-study-");
      items.push({
        label: t("caseStudies", "navbar") || "CASE STUDIES",
        href: "#cases",
        isLast: !isCaseView,
        isHome: false,
      });
    }

    return items;
  };

  const breadcrumbItems = getBreadcrumbItems();

  if (isFloatingHUD) {
    return (
      <div className="fixed bottom-6 left-6 z-50 select-none pointer-events-none hidden md:block">
        <div className="bg-white/90 backdrop-blur-md border border-gray-150 px-4 py-2.5 rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.06)] pointer-events-auto">
          <nav 
            aria-label="Breadcrumb" 
            className="flex items-center flex-wrap gap-1 md:gap-1.5 text-[10px] sm:text-xs font-mono"
            dir={isRTL ? "rtl" : "ltr"}
          >
            {breadcrumbItems.map((item, idx) => (
              <React.Fragment key={item.label + idx}>
                {idx > 0 && <span className="text-gray-300 mx-0.5 shrink-0 select-none">/</span>}
                <a
                  href={item.href}
                  onClick={(e) => {
                    if (item.isLast) {
                      e.preventDefault();
                    }
                  }}
                  className={`flex items-center gap-1 py-0.5 ${
                    item.isLast
                      ? "text-[#4f46e5] font-bold cursor-default"
                      : "text-gray-400 hover:text-[#4f46e5] hover:underline transition-colors font-medium text-slate-600"
                  }`}
                >
                  {item.isHome && (
                    <Home size={11} className={`shrink-0 ${item.isLast ? "text-[#4f46e5]" : "text-gray-400"} transition-colors`} />
                  )}
                  <span className="uppercase tracking-widest font-mono text-[9px] sm:text-[10px]">{item.label}</span>
                </a>
              </React.Fragment>
            ))}
          </nav>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="w-full bg-transparent relative z-30 select-none pb-10"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="flex items-center justify-between text-left relative z-10">
        
        {/* Dynamic Nav-Trail */}
        <nav aria-label="Breadcrumb" className="flex items-center flex-wrap gap-1.5 sm:gap-2 text-[11px] font-mono">
          {breadcrumbItems.map((item, idx) => (
            <React.Fragment key={item.label + idx}>
              {idx > 0 && <span className="text-gray-300 mx-0.5 shrink-0 select-none">/</span>}
              <a
                href={item.href}
                onClick={(e) => {
                  if (item.isLast) {
                    e.preventDefault();
                  }
                }}
                className={`flex items-center gap-1.5 py-1 ${
                  item.isLast
                    ? "text-[#4f46e5] font-bold cursor-default"
                    : "text-gray-400 hover:text-[#4f46e5] hover:underline transition-colors font-medium font-sans"
                }`}
              >
                {item.isHome && (
                  <Home size={12} className={`shrink-0 ${item.isLast ? "text-[#4f46e5]" : "text-gray-400"} transition-colors`} />
                )}
                <span className="uppercase tracking-widest font-mono">{item.label}</span>
              </a>
            </React.Fragment>
          ))}

          {/* Breadcrumb Part 3: Nested Article Detail */}
          <AnimatePresence>
            {isArticleView && blogTitle && (
              <motion.div
                initial={{ opacity: 0, x: isRTL ? 6 : -6 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: isRTL ? 6 : -6 }}
                transition={{ duration: 0.2 }}
                className="flex items-center gap-1.5 sm:gap-2 shrink-0 truncate max-w-[180px] sm:max-w-[300px]"
              >
                {/* Separator 2 */}
                <span className="text-gray-300 mx-0.5 shrink-0 select-none">/</span>
                
                <span className="text-[#4f46e5] font-bold flex items-center gap-1 truncate py-1">
                  <CornerDownRight size={11} className="text-[#4f46e5] shrink-0" />
                  <span className="truncate tracking-widest uppercase font-mono">
                    {blogTitle}
                  </span>
                </span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Breadcrumb Part 4: Nested Case Study Detail */}
          <AnimatePresence>
            {isCaseView && caseTitle && (
              <motion.div
                initial={{ opacity: 0, x: isRTL ? 6 : -6 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: isRTL ? 6 : -6 }}
                transition={{ duration: 0.2 }}
                className="flex items-center gap-1.5 sm:gap-2 shrink-0 truncate max-w-[180px] sm:max-w-[300px]"
              >
                {/* Separator 3 */}
                <span className="text-gray-300 mx-0.5 shrink-0 select-none">/</span>
                
                <span className="text-[#4f46e5] font-bold flex items-center gap-1 truncate py-1">
                  <CornerDownRight size={11} className="text-[#4f46e5] shrink-0" />
                  <span className="truncate tracking-widest uppercase font-mono">
                    {caseTitle}
                  </span>
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>

        {/* Back navigation button */}
        <a
          href={isArticleView ? "#blog" : isCaseView ? "#cases" : currentView === "leadership" ? "#about-company" : "#"}
          className="hidden sm:inline-flex items-center gap-1.5 text-[9.5px] font-mono tracking-widest text-gray-400 hover:text-[#4f46e5] transition-all cursor-pointer font-bold uppercase select-none group"
        >
          <ArrowRight 
            size={11} 
            className={`transform ${isRTL ? "group-hover:translate-x-1" : "rotate-180 group-hover:-translate-x-1"} transition-transform`} 
          />
          <span>{isArticleView ? "BACK" : isCaseView ? "BACK TO CASES" : currentView === "leadership" ? "BACK TO ABOUT" : "BACK TO HOME"}</span>
        </a>

      </div>
    </div>
  );
}
